import Phaser from 'phaser';

export class Chest {
  constructor(scene, x, y, amount, options = {}) {
    this.scene = scene;
    this.amount = amount;
    this.tokenSymbol = options?.tokenSymbol || scene?.tokenSymbol || 'SOULWARS';
    this.chain = options?.chain || null;
    
    // Health system
    this.maxHealth = 500; // Default chest health
    this.health = this.maxHealth;
    this.isDestroyed = false;
    
    // Create sprite
    const baseScale = 0.2;
    this.sprite = scene.add.image(x, y, 'loot-chest');
    this.sprite.setDepth(3);
    this.sprite.setScale(baseScale * (scene.objectScale || 1));
    
    // Store reference to this chest instance on the sprite
    this.sprite.chestInstance = this;
    
    // Add gentle bobbing animation
    scene.tweens.add({
      targets: this.sprite,
      y: y + 8,
      duration: 1200,
      ease: 'Sine.inOut',
      repeat: -1,
      yoyo: true,
    });
    
    // Create health bar (similar to players)
    this.createHealthBar();
    
    // UI camera should ignore chests
    if (scene.uiCamera && scene.uiCamera.ignore) {
      scene.uiCamera.ignore(this.sprite);
    }
  }
  
  createHealthBar() {
    if (!this.sprite) return;
    
    const barWidth = 40;
    const barHeight = 4;
    const offsetY = -25;
    
    // Health bar background
    this.healthBarBg = this.scene.add.rectangle(
      this.sprite.x,
      this.sprite.y + offsetY,
      barWidth,
      barHeight,
      0x000000,
      0.5
    );
    this.healthBarBg.setDepth(4);
    if (this.scene.uiCamera && this.scene.uiCamera.ignore) {
      this.scene.uiCamera.ignore(this.healthBarBg);
    }
    
    // Health bar fill
    this.healthBar = this.scene.add.rectangle(
      this.sprite.x,
      this.sprite.y + offsetY,
      barWidth,
      barHeight,
      0x00ff00,
      1
    );
    this.healthBar.setDepth(4);
    this.healthBar.setOrigin(0, 0.5);
    if (this.scene.uiCamera && this.scene.uiCamera.ignore) {
      this.scene.uiCamera.ignore(this.healthBar);
    }
    
    this.updateHealthBar();
  }
  
  updateHealthBar() {
    if (!this.healthBar || !this.healthBarBg || !this.sprite) return;
    
    const healthPercent = Math.max(0, Math.min(1, this.health / this.maxHealth));
    
    // Update health bar position (follows sprite position including bobbing)
    const offsetY = -25;
    this.healthBarBg.x = this.sprite.x;
    this.healthBarBg.y = this.sprite.y + offsetY;
    this.healthBar.x = this.sprite.x - this.healthBarBg.width / 2;
    this.healthBar.y = this.sprite.y + offsetY;
    
    // Update health bar scale
    this.healthBar.setScale(healthPercent, 1);
    
    // Update health bar color (green to red)
    const color = Phaser.Display.Color.Interpolate.ColorWithColor(
      Phaser.Display.Color.ValueToColor(0x00ff00),
      Phaser.Display.Color.ValueToColor(0xff0000),
      100,
      Math.floor(healthPercent * 100)
    );
    this.healthBar.setFillStyle(Phaser.Display.Color.GetColor(color.r, color.g, color.b), 1);
  }
  
  // Called each frame to update health bar position (follows sprite bobbing)
  update() {
    if (!this.isDestroyed && this.sprite) {
      this.updateHealthBar();
    }
  }
  
  takeDamage(amount) {
    if (this.isDestroyed) return 0;
    
    const actualDamage = Math.min(amount, this.health);
    this.health -= actualDamage;
    this.updateHealthBar();
    
    if (this.health <= 0) {
      this.destroy();
    }
    
    return actualDamage;
  }
  
  destroy() {
    if (this.isDestroyed) return;
    this.isDestroyed = true;
    
    const x = this.sprite.x;
    const y = this.sprite.y;
    
    // Burst tokens like player death
    if (this.scene && this.scene.lootSpawner) {
      // Calculate number of tokens to burst based on amount
      // Distribute the chest amount into multiple tokens
      const denominations = [50, 10, 5, 1];
      const tokens = this.splitAmountIntoCoins(this.amount, denominations);
      
      // Burst tokens in a circle
      const radius = 24;
      tokens.forEach((tokenAmount, i) => {
        const angle = (Math.PI * 2 * i) / Math.max(tokens.length, 6);
        const dx = x + Math.cos(angle) * radius * (1 + Math.random() * 1.5);
        const dy = y + Math.sin(angle) * radius * (1 + Math.random() * 1.5);
        
        const token = this.scene.lootSpawner.spawnTokenAt(x, y, tokenAmount, {
          tokenSymbol: this.tokenSymbol,
          chain: this.chain,
          denomination: tokenAmount,
        });
        
        if (token) {
          try {
            this.scene.tweens.add({
              targets: token,
              x: dx,
              y: dy,
              duration: 300,
              ease: 'Back.easeOut'
            });
          } catch (_) {}
        }
      });
      
      // Play chest open sound
      if (this.scene?.sfxManager?.play) {
        try {
          this.scene.sfxManager.play('chest-open');
        } catch (_) {}
      }
    }
    
    // Clean up sprites
    try {
      if (this.healthBar) this.healthBar.destroy();
      if (this.healthBarBg) this.healthBarBg.destroy();
      if (this.sprite) this.sprite.destroy();
    } catch (_) {}
  }
  
  splitAmountIntoCoins(total, denominations = [50, 10, 5, 1]) {
    const coins = [];
    if (total <= 0) return coins;
    const sorted = [...denominations].map(Number).filter((d) => Number.isFinite(d) && d > 0).sort((a, b) => b - a);
    const epsilon = 0.0001;
    let remaining = total;
    
    while (remaining > epsilon) {
      let matched = false;
      for (let i = 0; i < sorted.length; i++) {
        const denom = sorted[i];
        if (denom <= 0) continue;
        if (remaining + epsilon >= denom) {
          coins.push(denom);
          remaining -= denom;
          matched = true;
          break;
        }
      }
      if (!matched) {
        coins.push(Number(remaining.toFixed(4)));
        break;
      }
      if (coins.length > 5000) {
        // Prevent runaway loops
        coins.push(Number(Math.max(0, remaining).toFixed(4)));
        break;
      }
    }
    
    return coins;
  }
}

